import type { z } from 'zod';

import type { ReasonCode } from '../reason-code';

/** HTTP methods used by the Mini App API (technical spec §6). */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/** Statuses a Problem may carry in this contract (C1 spec R9). */
export type ProblemStatus = 400 | 401 | 404 | 409 | 422 | 429 | 500 | 503;

/** Register question (`Q34`); a new question gets its register number before it is cited. */
export type QuestionId = `Q${number}`;

/** A schema and the component name it gets in openapi.json. */
export interface NamedSchema {
  readonly name: string;
  readonly schema: z.ZodType;
}

/** One failure a route may return: HTTP status and stable reason code (master X2). */
export interface RouteError {
  readonly status: ProblemStatus;
  readonly reasonCode: ReasonCode;
}

/** What a route returns on success. */
export type RouteSuccess =
  | { readonly kind: 'json'; readonly status: 200 | 201 | 202; readonly body: NamedSchema }
  | { readonly kind: 'empty'; readonly status: 204 }
  | { readonly kind: 'binary'; readonly status: 200; readonly contentType: 'image/jpeg' };

/** Marks a route whose shape depends on open questions (C1 spec R13). */
export interface Provisional {
  readonly questions: readonly QuestionId[];
  readonly note: string;
}

/** Master X3: none for reads, required for writes unless exempted with a written reason. */
export type IdempotencyRule =
  | { readonly kind: 'none' }
  | { readonly kind: 'required' }
  | { readonly kind: 'exempt'; readonly reason: string };

/** Input of {@link defineRoute}. */
export interface RouteInput<Id extends string> {
  readonly operationId: Id;
  readonly method: HttpMethod;
  readonly path: `/${string}`;
  readonly tag: string;
  readonly summary: string;
  readonly description: string;
  /** Upstream source of the route, cited in openapi.json (standards §4). */
  readonly source: string;
  readonly auth: 'none' | 'bearer';
  readonly provisional?: Provisional;
  /** Reason a write needs no Idempotency-Key. Reviewed in the spec PR (C1 spec R7). */
  readonly idempotencyExemption?: string;
  readonly params?: z.ZodObject;
  readonly query?: z.ZodObject;
  readonly body?: NamedSchema;
  readonly success: RouteSuccess;
  /** Route-specific errors; the standard ones (spec R9) are added by {@link defineRoute}. */
  readonly errors?: readonly RouteError[];
}

/** A resolved route: the single source for openapi.json, fixtures and MSW handlers. */
export interface RouteContract<Id extends string = string> {
  readonly operationId: Id;
  readonly method: HttpMethod;
  readonly path: `/${string}`;
  readonly tag: string;
  readonly summary: string;
  readonly description: string;
  readonly source: string;
  readonly auth: 'none' | 'bearer';
  readonly provisional: Provisional | null;
  readonly idempotency: IdempotencyRule;
  readonly params: z.ZodObject | null;
  readonly query: z.ZodObject | null;
  readonly body: NamedSchema | null;
  readonly success: RouteSuccess;
  readonly errors: readonly RouteError[];
}

/** True for POST, PUT and DELETE (master X3). */
export function isWriteMethod(method: HttpMethod): boolean {
  return method !== 'GET';
}

/**
 * Resolves a route: checks path parameters against the params schema, derives the idempotency
 * rule, and adds the standard errors of spec R9 before the route-specific ones.
 * @throws Error when the `{param}` names in the path differ from the params schema keys.
 */
export function defineRoute<const Id extends string>(input: RouteInput<Id>): RouteContract<Id> {
  assertPathParamsMatch(input.path, input.params);
  const idempotency = resolveIdempotency(input.method, input.idempotencyExemption);
  return {
    operationId: input.operationId,
    method: input.method,
    path: input.path,
    tag: input.tag,
    summary: input.summary,
    description: input.description,
    source: input.source,
    auth: input.auth,
    provisional: input.provisional ?? null,
    idempotency,
    params: input.params ?? null,
    query: input.query ?? null,
    body: input.body ?? null,
    success: input.success,
    errors: collectErrors(input, idempotency),
  };
}

/** Full OpenAPI description: provisional banner, text, source and reason codes (standards §4). */
export function describeRoute(route: RouteContract): string {
  const banner =
    route.provisional === null
      ? ''
      : `**Provisional** (${route.provisional.questions.join(', ')}): ${route.provisional.note}\n\n`;
  const codes = route.errors.map((error) => error.reasonCode).join(', ');
  return `${banner}${route.description}\n\nSource: ${route.source}.\n\nPossible reasonCode values: ${codes}.`;
}

function resolveIdempotency(method: HttpMethod, exemption: string | undefined): IdempotencyRule {
  if (!isWriteMethod(method)) {
    return { kind: 'none' };
  }
  // Master X3: every write needs a key; an exemption must state why (C1 spec R7).
  return exemption === undefined ? { kind: 'required' } : { kind: 'exempt', reason: exemption };
}

function collectErrors<Id extends string>(
  input: RouteInput<Id>,
  idempotency: IdempotencyRule,
): RouteError[] {
  const errors: RouteError[] = [];
  const add = (status: ProblemStatus, reasonCode: ReasonCode): void => {
    if (!errors.some((error) => error.reasonCode === reasonCode)) {
      errors.push({ status, reasonCode });
    }
  };
  if (input.params !== undefined || input.query !== undefined || input.body !== undefined) {
    add(400, 'VALIDATION_FAILED');
  }
  if (idempotency.kind === 'required') {
    // A missing key is IDEMPOTENCY_KEY_REQUIRED; a malformed or repeated one is VALIDATION_FAILED,
    // even on a write with no body (DELETE /trading-account, POST /telegram/logout). Found when
    // the API's real openapi.json was pinned (ADR 0003, Q95).
    add(400, 'VALIDATION_FAILED');
    add(400, 'IDEMPOTENCY_KEY_REQUIRED');
    add(409, 'IDEMPOTENCY_KEY_REUSED');
    // S2 R10: a concurrent retry of a key still being processed (spec R9, proposal).
    add(409, 'IDEMPOTENCY_KEY_IN_PROGRESS');
  }
  if (input.auth === 'bearer') {
    add(401, 'UNAUTHENTICATED');
    add(401, 'TOKEN_EXPIRED');
  }
  // Standards §5.4: another trader's resource answers 404, never 403, so nothing leaks (I8).
  if (input.params !== undefined) {
    add(404, 'NOT_FOUND');
  }
  // Spec R9: every protected route reads the API database, so it may answer 503. On the
  // emergency stop this covers the API database only, never an engine port (M4 R10, AC19).
  if (input.auth === 'bearer') {
    add(503, 'SERVICE_UNAVAILABLE');
  }
  for (const error of input.errors ?? []) {
    add(error.status, error.reasonCode);
  }
  add(500, 'INTERNAL_ERROR');
  return errors;
}

function assertPathParamsMatch(path: string, params: z.ZodObject | undefined): void {
  const inPath = [...path.matchAll(/\{(\w+)\}/g)].flatMap((match) =>
    match[1] === undefined ? [] : [match[1]],
  );
  const declared = params === undefined ? [] : Object.keys(params.shape);
  if (inPath.join(',') !== declared.join(',')) {
    throw new Error(
      `Route ${path}: path parameters [${inPath.join(', ')}] do not match the params schema [${declared.join(', ')}]`,
    );
  }
}
